// weeks.ts - 주차(week-of-month) 버킷팅 순수 로직.
// 주간업무보고가 월요일 기준이라 "그 주 월요일이 속한 달의 N주차"로 센다:
// 예) 2026-06-29(월)~07-04(토) → 6월 5주, 07-05(일)부터 7월 1주.
// dates.ts 와 같은 규칙으로 로컬 필드만 쓴다(UTC 드리프트 없음).
import { addDays, isoDate, parseIsoDate } from "./dates";

function mondayOfWeek(d: Date): Date {
  return addDays(new Date(d.getFullYear(), d.getMonth(), d.getDate()), 1 - d.getDay());
}

// 월요일끼리는 7일 간격이라 N번째 월요일 = ceil(월요일 일자 / 7).
export function weekOfMonth(d: Date): number {
  return Math.ceil(mondayOfWeek(d).getDate() / 7);
}

export interface WeekBucket {
  key: string; // 정렬·비교용 "YYYY-MM-W" (예 "2026-06-5")
  year: number;
  month: number; // 1-12
  week: number; // 1..5
  label: string; // "YYYY년 M월 N주"
}

export function bucketOf(d: Date): WeekBucket {
  const monday = mondayOfWeek(d);
  const year = monday.getFullYear();
  const month = monday.getMonth() + 1;
  const week = Math.ceil(monday.getDate() / 7);
  return {
    key: `${year}-${String(month).padStart(2, "0")}-${week}`,
    year,
    month,
    week,
    label: `${year}년 ${month}월 ${week}주`,
  };
}

export function weekLabel(d: Date): string {
  return bucketOf(d).label;
}

// ISO date 문자열("YYYY-MM-DD")의 주차 버킷. 시간대 무시(로컬 자정 기준).
export function bucketOfIso(iso: string): WeekBucket {
  return bucketOf(parseIsoDate(iso));
}

// start~end(포함) 사이의 주차 버킷을 순서대로. 하루씩 전진하며 distinct key 수집.
export function weeksInRange(start: Date, end: Date): WeekBucket[] {
  const out: WeekBucket[] = [];
  const seen = new Set<string>();
  let cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const stop = isoDate(end);
  // ponytail: 일 단위 루프 - O(days). 대시보드 조회 범위는 수개월이라 무방.
  for (let guard = 0; guard < 3700; guard++) {
    const b = bucketOf(cur);
    if (!seen.has(b.key)) {
      seen.add(b.key);
      out.push(b);
    }
    if (isoDate(cur) >= stop) break;
    cur = addDays(cur, 1);
  }
  return out;
}

// 주차 키별 건수 - weeks 순서대로, 해당 주에 항목이 없으면 0.
// pick 은 항목의 기준 날짜(ISO)를 뽑는다 - 도메인(등록일/완료일/마일스톤일)은 호출부가 정한다.
export function countsPerWeek<T>(
  items: readonly T[],
  weeks: readonly WeekBucket[],
  pick: (item: T) => string
): { bucket: WeekBucket; count: number }[] {
  const byKey: Record<string, number> = {};
  for (const it of items) {
    const k = bucketOfIso(pick(it)).key;
    byKey[k] = (byKey[k] || 0) + 1;
  }
  return weeks.map((bucket) => ({ bucket, count: byKey[bucket.key] || 0 }));
}
